import { MarketCompetitor, MarketData, MarketSeriesPoint } from '../../../common/hooks/use-market-api.hook';

/**
 * Small helpers the Business pages share: domains, links, months, shares.
 *
 * No copy lives here any more. The sentences a vendor reads are built in
 * `market-insights.ts` as translation keys and numbers, so every language
 * gets the same reading of the same arithmetic.
 */

/** 'https://www.Stark.com/rugs?a=1' → 'stark.com'. */
export const cleanDomain = (input?: string | null): string =>
    (input || '')
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split(/[/?#]/)[0];

/** A bare domain is a relative path to a browser; give it a scheme. */
export const externalHref = (url: string): string =>
    /^https?:\/\//i.test(url) ? url : `https://${url.replace(/^\/+/, '')}`;

/** A share of the total, for bars that need a width (0–100, rounded). */
export const share = (part?: number | null, total?: number | null): number => {
    if (!part || !total || total <= 0) return 0;
    return Math.min(100, Math.round((part / total) * 100));
};

/** Month labels a person reads: "2026-09" is not a date to anyone, and "Sep 26" reads as one — so "Sep 2026". */
export const monthLabel = (month: string, locale = 'en'): string => {
    const [y, m] = (month || '').split('-').map(Number);
    if (!y || !m) return month;
    try {
        return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(new Date(y, m - 1, 1));
    } catch {
        return month;
    }
};

/** One numeric field of a snapshot series, oldest first; rows without it are skipped. */
export const seriesOf = (points: MarketSeriesPoint[] | undefined, key: string) =>
    (points ?? [])
        .filter((p) => typeof p.summary?.[key] === 'number' && Number.isFinite(p.summary[key]))
        .map((p) => ({ month: p.month, value: p.summary[key] as number }));

/** The competitors worth naming, biggest overlap first. */
export function rankedCompetitors(data: Pick<MarketData, 'competitors'>): MarketCompetitor[] {
    const all = data.competitors?.competitors ?? [];
    return [...all].filter((c) => !!c.domain).sort((a, b) => (b.sharedKeywords ?? 0) - (a.sharedKeywords ?? 0));
}
